import { HorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';
import { Socials } from 'components/Socials/Socials';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { ChevronIcon } from 'icons/chevron';
import React, { MouseEventHandler, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { Link } from 'react-router-dom';
import cls from 'classnames';

import styles from './NavbarMenu.module.scss';
import { SectionLink } from 'routing/siteMap';
import { UpRightArrow } from 'components/Icons/Icons';
import { useFlags } from 'hooks/useFlags';
import { IsFlagEnabled } from 'components/FeatureFlagged/FeatureFlagged';

export type NavMenuLinks = {
  title: string;
  path: string;
  external: boolean;
};

export type NavMenuItem = {
  title: string;
  description: string;
  img: string;
  links: SectionLink[];
};

export type DropdownNavMenuProps = NavMenuItem & {
  name: string;
};

export function DropdownNavMenu({ name, title, description, img, links }: DropdownNavMenuProps) {
  const item = (
    <div className={styles.dropdownItem}>
      <li className={styles.navItem}>{name}</li>
      <ChevronIcon />
    </div>
  );

  return (
    <Tooltip className={styles.dropdownMenu} icon={item}>
      <div className={styles.menuContent}>
        <div className={styles.menuDescription}>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        <LazyLoadImage className={styles.menuImage} alt={name} src={img} />
        <div className={styles.menuLinks}>
          <ul>
            {links.map((x, i) => (
              <NavbarItem key={i} {...x} />
            ))}
          </ul>
          <div>
            <HorizontalDivider />
            <Socials />
          </div>
        </div>
      </div>
    </Tooltip>
  );
}

export type ItemProps = React.LiHTMLAttributes<HTMLLIElement> & {
  name: string;
  path: string;
  external?: boolean;
  hideWhen?: (isFlagEnabled: IsFlagEnabled) => boolean;
};

export function NavbarItem({ name, path, external, hideWhen, ...props }: ItemProps) {
  const { isFlagEnabled } = useFlags();

  if (hideWhen && hideWhen(isFlagEnabled)) {
    return null;
  }

  return external ? (
    <a
      href={path}
      target="_blank"
      rel="noreferrer"
      className={styles.navLink}
      onClick={() => props.onClick}
    >
      <li className={cls(styles.navItem, props.className)}>{name}</li>
      {external && <UpRightArrow />}
    </a>
  ) : (
    <Link key={name} to={path} className={styles.navLink}>
      <li className={cls(styles.navItem, props.className)} onClick={props.onClick}>
        {name}
      </li>
    </Link>
  );
}

export type AccordionNavMenuProps = NavMenuItem & {
  name: string;
  onClickItem: MouseEventHandler;
};

export const AccordionNavMenu = ({ name, links, onClickItem }: AccordionNavMenuProps) => {
  const [showItems, setShowItems] = useState(false);

  return (
    <div className={styles.accordionMenuWrapper}>
      <div>
        <button className={styles.menuButton} onClick={() => setShowItems(!showItems)}>
          {name}
          <ChevronIcon />
        </button>
      </div>
      {showItems &&
        links.map((item) => {
          return (
            <>
              <HorizontalDivider className={styles.accordionDivider} />
              <NavbarItem onClick={onClickItem} {...item} />
            </>
          );
        })}
    </div>
  );
};
